import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';

import replaceSurveySectionIds from '../../utilities/Survey';
import FilterSelect from '../../trial/FilterSelect';
import ApiClient from '../../../loopback/apiClient';
import { getYearsApartInArray } from '../../utilities/Sorting';
import {
  fetchTrialsList,
  setYearFilter,
  updateSections,
  showErrorNotification,
} from '../../../redux/actions';
import { RootState, Survey, Trial } from '../../../redux/types';

type Props = PropsFromRedux & {
  disabled?: boolean;
}

const SurveyDuplicate: React.FC<Props> = ({
  yearFilter,
  trialList,
  disabled = false,
  fetchTrialsList: fetchTrialsListAction,
  setYearFilter: setYearFilterAction,
  updateSections: updateSectionsAction,
  showErrorNotification: showErrorNotificationAction,
}: Props) => {
  const [selectedTrialId, setSelectedTrialId] = useState<number|''>('');

  const onTrialSelect = (
    event: SelectChangeEvent<number | ''>,
  ) => {
    const trial = event.target.value;
    setSelectedTrialId(trial as number);
  };

  const onYearSelect = (year: string) => {
    setSelectedTrialId('');
    setYearFilterAction(year);
    fetchTrialsListAction();
  };

  const onCopySurvey = () => {
    if (!selectedTrialId) {
      return;
    }
    ApiClient.getSurveyByTrialId(selectedTrialId)
      .then((survey: Survey) => {
        if (!survey || (survey.version && survey.version <= 2)) {
          showErrorNotificationAction(<FormattedMessage id="trialmanagement.surveyUnavailable" />);
        } else {
          const replacedIdSections = replaceSurveySectionIds(survey);
          updateSectionsAction(replacedIdSections);
        }
      })
      .catch((e: Error) => {
        showErrorNotificationAction(e.message);
      });
  };

  return disabled ? null : (
    <Box className="pv2 pl3" flexGrow={1}>
      <h3><FormattedMessage id="surveyDuplicate.replicateSurvey" /></h3>
      <Box display="flex">
        <FilterSelect
          id="TrialSearchType-yearselect"
          options={getYearsApartInArray()}
          value={yearFilter}
          label={<FormattedMessage id="trialSearch.yearSelectHeading" />}
          onChange={onYearSelect}
        />
        <FormControl sx={{ width: '250px' }}>
          <InputLabel>Survey</InputLabel>
          <Select
            labelId="trial-copy-select"
            id="trial-copy-select"
            value={selectedTrialId}
            onChange={onTrialSelect}
          >
            {
              [
                <MenuItem key="blank" value="">
                  None
                </MenuItem>,
              ].concat(
                trialList
                  .map((trialObj: Trial) => (
                    <MenuItem key={trialObj.id} value={trialObj.id}>
                      {trialObj.name}
                      {' '}
                      (
                      {trialObj.code}
                      )
                    </MenuItem>
                  )),
              )
            }
          </Select>
        </FormControl>
        <Button
          sx={{ marginLeft: '5px' }}
          variant="contained"
          onClick={onCopySurvey}
        >
          <FormattedMessage id="surveyDuplicate.copySurvey" />
        </Button>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  yearFilter: state.trial.yearFilter,
  trialList: state.trial.trialList,
  locale: state.intl.locale,
});

const dispatchProps = {
  fetchTrialsList,
  setYearFilter,
  updateSections,
  showErrorNotification,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(SurveyDuplicate);
